import React, { forwardRef, useCallback } from 'react';
import { useField } from 'formik';
import { Check } from 'lucide-react';

interface ICheckboxField {
  label: string | React.ReactNode;
  name: string;
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  disabled?: boolean;
  required?: boolean;
  error?: string;
  touched?: boolean;
  className?: string;
  checkboxClassName?: string;
  labelClassName?: string;
  description?: string;
}

// Base Checkbox Component (Standalone)
const BaseCheckboxField = forwardRef<HTMLInputElement, ICheckboxField>(
  (
    {
      label,
      name,
      checked = false,
      onChange,
      disabled = false,
      required = false,
      error = '',
      touched = false,
      className = '',
      checkboxClassName = '',
      labelClassName = '',
      description,
      onBlur,
    },
    ref
  ) => {
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(e.target.checked);
    };

    const displayError = touched && error;

    return (
      <div className={className}>
        <div className="flex items-start gap-3">
          {/* Custom Checkbox */}
          <div className="relative flex items-center pt-0.5">
            <input
              ref={ref}
              type="checkbox"
              id={name}
              name={name}
              checked={checked}
              onChange={handleChange}
              onBlur={onBlur}
              disabled={disabled}
              className="sr-only peer"
            />
            <label
              htmlFor={name}
              className={`
                w-5 h-5 border-2 rounded cursor-pointer flex items-center justify-center
                transition-all duration-200 flex-shrink-0
                ${
                  checked
                    ? 'bg-blue-600 border-blue-600'
                    : 'bg-white border-gray-300 hover:border-blue-400'
                }
                ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                ${displayError ? 'border-red-500' : ''}
                peer-focus:ring-2 peer-focus:ring-blue-300 peer-focus:ring-offset-2
                ${checkboxClassName}
              `}
            >
              {checked && (
                <Check
                  size={14}
                  className="text-white"
                  strokeWidth={3}
                />
              )}
            </label>
          </div>

          {/* Label and Description */}
          <div className="flex-1 min-w-0">
            <label
              htmlFor={name}
              className={`
                text-sm font-medium text-gray-900 cursor-pointer select-none
                ${disabled ? 'text-gray-500 cursor-not-allowed' : ''}
                ${labelClassName}
              `}
            >
              {label}
              {required && <span className="text-red-500 ml-1">*</span>}
            </label>

            {description && (
              <p className="mt-1 text-xs text-gray-500">{description}</p>
            )}
          </div>
        </div>

        {displayError && (
          <p
            className="text-red-500 text-sm mt-1 ml-8"
            id={`${name}-error`}
          >
            {error}
          </p>
        )}
      </div>
    );
  }
);

BaseCheckboxField.displayName = 'BaseCheckboxField';

// Formik-compatible Checkbox Component
type FormikCheckboxFieldProps = Omit<
  ICheckboxField,
  'checked' | 'onChange' | 'error' | 'touched'
>;

const FormikCheckboxField = forwardRef<
  HTMLInputElement,
  FormikCheckboxFieldProps
>((props, ref) => {
  const { name, ...rest } = props;
  const [{ value }, { touched, error }, { setValue, setTouched }] =
    useField<boolean>(name);

  const handleChange = useCallback(
    (checked: boolean) => {
      setValue(checked, true);
    },
    [setValue]
  );

  const handleBlur = useCallback(() => {
    setTouched(true, true);
  }, [setTouched]);

  return (
    <BaseCheckboxField
      ref={ref}
      name={name}
      checked={value}
      onChange={handleChange}
      onBlur={handleBlur}
      error={error}
      touched={touched}
      {...rest}
    />
  );
});

FormikCheckboxField.displayName = 'FormikCheckboxField';

export { BaseCheckboxField as CheckboxField, FormikCheckboxField };
export default FormikCheckboxField;
